import { useState, useCallback, useRef } from "react";
import { roomMessageService } from "../services/rooms";

/**
 * useJumpToMessage Hook
 *
 * Industry-standard implementation for jump-to-message functionality
 * like WhatsApp, Telegram, and Slack.
 *
 * Features:
 * - Jump to any message (search result, pinned, or reply)
 * - Load context (25 before + target + 25 after)
 * - Bidirectional infinite scroll from jumped position
 * - Track position to prevent duplicate loading
 */
const useJumpToMessage = (roomId) => {
  // Jump state
  const [jumpedMessages, setJumpedMessages] = useState([]);
  const [targetMessageId, setTargetMessageId] = useState(null);
  const [targetIndex, setTargetIndex] = useState(null);
  const [hasOlder, setHasOlder] = useState(false);
  const [hasNewer, setHasNewer] = useState(false);
  const [cursors, setCursors] = useState({ older: null, newer: null });
  const [isJumpedMode, setIsJumpedMode] = useState(false);
  const [isJumping, setIsJumping] = useState(false);

  // Loading refs to prevent duplicate requests
  const loadingRef = useRef({ older: false, newer: false });

  /**
   * Jump to a specific message (search result, pinned, or reply)
   * Loads the target message with 25 messages before and after
   */
  const jumpToMessage = useCallback(
    async (messageId) => {
      if (!roomId || !messageId) return null;

      setIsJumping(true);
      try {
const response = await roomMessageService.getMessageContext(
          roomId,
          messageId,
          25,
          25
        );

        if (response.success) {
          const { data, target, pagination } = response;
setJumpedMessages(data);
          setTargetMessageId(messageId);
          setTargetIndex(target.index);
          setHasOlder(pagination.hasOlder);
          setHasNewer(pagination.hasNewer);
          setCursors(pagination.cursors || { older: null, newer: null });
          setIsJumpedMode(true);

          return target.index; // Return index for scrolling
        }
      } catch (error) {
return null;
      } finally {
        setIsJumping(false);
      }

      return null;
    },
    [roomId]
  );

  /**
   * Load older messages (scroll up in jumped mode)
   */
  const loadOlder = useCallback(async () => {
    if (loadingRef.current.older || !hasOlder || !cursors.older) {
return null;
    }

    loadingRef.current.older = true;
try {
      const response = await roomMessageService.loadMoreFromPosition(
        roomId,
        cursors.older,
        "older",
        50
      );

      if (response.success) {
        const { data, pagination } = response;
setJumpedMessages((prev) => {
          // Deduplicate
          const combined = [...data, ...prev];
          const unique = combined.filter(
            (msg, index, self) =>
              index === self.findIndex((m) => m._id === msg._id)
          );
          return unique;
        });

        // Adjust target index since we prepended messages
        setTargetIndex((prev) => (prev !== null ? prev + data.length : null));

        setHasOlder(pagination.hasMore);
        setCursors((prev) => ({
          ...prev,
          older: pagination.nextCursor || null,
        }));

        return data.length; // Return count for scroll adjustment
      }
    } catch (error) { /* ignored */ } finally {
      loadingRef.current.older = false;
    }

    return null;
  }, [roomId, hasOlder, cursors.older]);

  /**
   * Load newer messages (scroll down in jumped mode)
   */
  const loadNewer = useCallback(async () => {
    if (loadingRef.current.newer || !hasNewer || !cursors.newer) {
return null;
    }

    loadingRef.current.newer = true;
try {
      const response = await roomMessageService.loadMoreFromPosition(
        roomId,
        cursors.newer,
        "newer",
        50
      );

      if (response.success) {
        const { data, pagination } = response;
setJumpedMessages((prev) => {
          // Deduplicate
          const combined = [...prev, ...data];
          const unique = combined.filter(
            (msg, index, self) =>
              index === self.findIndex((m) => m._id === msg._id)
          );
          return unique;
        });

        setHasNewer(pagination.hasMore);
        setCursors((prev) => ({
          ...prev,
          newer: pagination.nextCursor || null,
        }));

        return data.length;
      }
    } catch (error) { /* ignored */ } finally {
      loadingRef.current.newer = false;
    }

    return null;
  }, [roomId, hasNewer, cursors.newer]);

  /**
   * Exit jumped mode and return to normal chat view (latest messages)
   */
  const exitJumpedMode = useCallback(() => {
setJumpedMessages([]);
    setTargetMessageId(null);
    setTargetIndex(null);
    setHasOlder(false);
    setHasNewer(false);
    setCursors({ older: null, newer: null });
    setIsJumpedMode(false);
  }, []);

  /**
   * Clear highlight after animation
   */
  const clearHighlight = useCallback(() => {
    setTargetMessageId(null);
    setTargetIndex(null);
  }, []);

  return {
    // State
    jumpedMessages,
    targetMessageId,
    targetIndex,
    hasOlder,
    hasNewer,
    isJumpedMode,
    isJumping,
    cursors,

    // Actions
    jumpToMessage,
    loadOlder,
    loadNewer,
    exitJumpedMode,
    clearHighlight,
  };
};

export default useJumpToMessage;
